/**
 * PaymentsPanel.jsx - Finance Invoice > Payments: the money received, by
 * the day it came in, over a range (this week by default - the Dashboard's
 * "$ collected" box reads the same payments). A row opens its invoice.
 */
import { useCallback, useEffect, useState } from 'react';
import { latestReads, useLiveRefresh } from '../../live/useLive';
import { exportCsv } from '../../utils/exportCsv';
import { fmtDate } from '../../utils/format';
import { addDays, dayIn, lastWeekRange } from '../dashboard/lastWeek';
import { Msg, num, td, th } from '../spareParts/spUi';
import { Btn, GREEN, LINE, MUTED, TextInput, fmtMoney } from '../quotes/quoteUi';
import { errorOf, listPayments } from './financeInvoicesApi';
import { numberLabel, projectHref, projectLabel } from './invoiceRules';

/** This Monday-Sunday week and the one before, in GLOBAL's time zone (the Dashboard's weeks). */
export function weeks(today = dayIn()) {
  const last = lastWeekRange(today);
  const thisFrom = addDays(last.from, 7);
  return { this: { from: thisFrom, to: addDays(thisFrom, 6) }, last };
}

export default function PaymentsPanel({ tick = 0, onOpen }) {
  const [range, setRange] = useState(() => weeks().this);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [reads] = useState(latestReads);
  const load = useCallback(async () => {
    if (!range.from || !range.to) return;
    const t = reads.start();
    const res = await listPayments(range.from, range.to);
    if (!reads.show(t)) return;
    if (!res.ok) {
      setMsg({ kind: 'error', text: errorOf(res, 'Could not load the payments.') });
      return;
    }
    setMsg(null);
    setData(res.body);
  }, [range, reads]);
  useEffect(() => {
    load();
  }, [load, tick]);
  useLiveRefresh('finance-invoices', load);

  const w = weeks();
  const pick = (r) => setRange(r);
  const on = (r) => r.from === range.from && r.to === range.to;
  const csv = () =>
    exportCsv(
      `payments-${range.from}-to-${range.to}.csv`,
      data.payments.map((p) => ({
        Received: p.paid_on,
        'Invoice #': numberLabel(p),
        Customer: p.customer || '',
        Project: p.project_name || '',
        'Quote #': p.quote_letter || '',
        Method: p.method || '',
        Reference: p.reference || '',
        Notes: p.notes || '',
        Amount: Number(p.amount).toFixed(2),
        'Entered by': p.created_by || '',
      }))
    );
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <Btn small primary={on(w.this)} onClick={() => pick(w.this)}>
          This week
        </Btn>
        <Btn small primary={on(w.last)} onClick={() => pick(w.last)}>
          Last week
        </Btn>
        <TextInput
          aria-label="Received from"
          type="date"
          value={range.from}
          onChange={(v) => setRange((r) => ({ ...r, from: v }))}
          style={{ width: 150 }}
        />
        <span style={{ fontSize: 11, color: MUTED }}>to</span>
        <TextInput
          aria-label="Received to"
          type="date"
          value={range.to}
          onChange={(v) => setRange((r) => ({ ...r, to: v }))}
          style={{ width: 150 }}
        />
        {data && (
          <span style={{ fontSize: 12.5, fontWeight: 800, color: GREEN, marginLeft: 8 }} data-testid="collected-total">
            $ collected: {fmtMoney(data.total)} · {data.count} {data.count === 1 ? 'payment' : 'payments'}
          </span>
        )}
        {data && (
          <Btn small onClick={csv} disabled={!data.payments.length} style={{ marginLeft: 'auto' }}>
            Export CSV
          </Btn>
        )}
      </div>
      <Msg msg={msg} />
      {!data && !msg && <div style={{ padding: 30, color: MUTED, fontSize: 12 }}>Loading the payments...</div>}
      {data && (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, overflow: 'auto', background: '#fff' }}>
          <table aria-label="Payments received" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Received</th>
                <th style={th}>Invoice #</th>
                <th style={th}>Customer</th>
                <th style={th}>Project</th>
                <th style={th}>Method / ref</th>
                <th style={th}>Entered by</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((p) => (
                <tr
                  key={p.id}
                  tabIndex={0}
                  className="go-focus"
                  style={{ cursor: 'pointer' }}
                  title="Open its invoice"
                  onClick={() => onOpen(p.invoice_id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onOpen(p.invoice_id);
                  }}
                >
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(p.paid_on)}</td>
                  <td style={{ ...td, fontFamily: 'JetBrains Mono, Consolas, monospace', fontWeight: 700, color: GREEN }}>
                    {numberLabel(p)}
                  </td>
                  <td style={td}>{p.customer || '-'}</td>
                  <td style={td}>
                    <a href={projectHref(p)} onClick={(e) => e.stopPropagation()} style={{ color: '#1F2328', fontWeight: 600 }}>
                      {projectLabel(p)}
                    </a>
                  </td>
                  <td style={td}>{[p.method, p.reference].filter(Boolean).join(' · ') || '-'}</td>
                  <td style={td}>{p.created_by || '-'}</td>
                  <td style={num}>{fmtMoney(p.amount)}</td>
                </tr>
              ))}
              {!data.payments.length && (
                <tr>
                  <td colSpan={7} style={{ ...td, textAlign: 'center', color: MUTED, padding: 24 }}>
                    No payments received {fmtDate(range.from)} - {fmtDate(range.to)}.
                  </td>
                </tr>
              )}
            </tbody>
            {data.payments.length > 0 && (
              <tfoot>
                <tr style={{ background: '#F6F9F3', fontWeight: 800 }}>
                  <td style={td} colSpan={6}>
                    Total · {data.count} {data.count === 1 ? 'payment' : 'payments'}
                  </td>
                  <td style={num}>{fmtMoney(data.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
